import Document from "@tiptap/extension-document";
import Link from "@tiptap/extension-link";
import Paragraph from "@tiptap/extension-paragraph";
import Text from "@tiptap/extension-text";
import { UndoRedo } from "@tiptap/extensions";
import { EditorContent, EditorOptions, useEditor } from "@tiptap/react";
import { escape } from "lodash";
import { forwardRef, ReactNode, useEffect, useRef } from "react";

import { Affix, AutocompleteExtension, MentionsExtension } from "./extensions";
import { TextEditorRoot } from "./TextEditorRoot";

export interface TextEditorProps {
  placeholder?: ReactNode; // Note: we can't use the tiptap placeholder extension because it supports only string
  prefix?: string;
  suffix?: string;
  suggestions?: string[];
  mentions?: string[];
  allowCreateMentions?: boolean;
  value: EditorOptions["content"];
  onChange: (newValue: TextEditorProps["value"], text: string) => void;
  onBlur?: () => void;
  onPasteImage?: (event: ClipboardEvent) => void;
  onSubmit?: () => void;
  disabled?: boolean;
  disableBrowserAutocomplete?: boolean;
  autofocus?: boolean;
  testId?: string;
}

export const classes = {
  root: "TextEditor-root",
};

export const TextEditor = forwardRef<HTMLDivElement, TextEditorProps>(
  (props, ref) => {
    const {
      mentions,
      allowCreateMentions,
      placeholder,
      prefix,
      suffix,
      suggestions,
      value,
      onChange,
      onBlur,
      onPasteImage,
      disabled,
      disableBrowserAutocomplete,
      autofocus,
      onSubmit,
      testId,
    } = props;

    const lastEmittedChange = useRef<string>();

    // Refs break the stale closure in useEditor extensions (which capture values once on init).
    // useRef ignores its argument after the first render, so .current= is the actual sync.
    const mentionsRef = useRef<string[]>([]);
    mentionsRef.current = mentions ?? [];

    const suggestionsRef = useRef<string[]>([]);
    suggestionsRef.current = suggestions ?? [];

    const handleFocus = () => {
      if (!editor.isEmpty) return;

      if (!prefix && !suffix) return;

      let newContent = "";
      let newCursorPosition = 0;

      const escapedPrefix = escape(prefix);
      const escapedSuffix = escape(suffix);

      if (prefix && suffix) {
        newContent = `<affix data-original-value="${escapedPrefix}">${escapedPrefix}</affix><p></p><affix data-original-value="${escapedSuffix}">${escapedSuffix}</affix>`;
        newCursorPosition = prefix.length + 3;
      } else if (prefix) {
        newContent = `<affix data-original-value="${escapedPrefix}">${escapedPrefix}</affix><p></p>`;
        newCursorPosition = prefix.length + 3;
      } else if (suffix) {
        newContent = `<p></p><affix data-original-value="${escapedSuffix}">${escapedSuffix}</affix>`;
        newCursorPosition = 1;
      }

      editor.commands.setContent(newContent);

      if (newCursorPosition > 0) {
        setTimeout(() => {
          editor.commands.setTextSelection(newCursorPosition);
        }, 0);
      }
    };

    const editor = useEditor({
      extensions: [
        // Core extensions
        Document,
        Link,
        Paragraph,
        Text,
        UndoRedo,
        // Custom extensions
        Affix,
        MentionsExtension.configure({
          getMentions: () => mentionsRef.current,
          allowCreate: allowCreateMentions,
        }),
        AutocompleteExtension.configure({
          getSuggestions: () => suggestionsRef.current,
        }),
      ],
      content: value,
      autofocus: autofocus ? "end" : false,
      editable: !disabled,
      onUpdate: ({ editor: _editor }) => {
        // Warning: For every keystroke, we call JSON.stringify, which can be a performance issue. Currently, this is acceptable since we use it only for relatively short prompt inputs.
        const json = _editor.getJSON();
        const jsonString = JSON.stringify(json);

        if (jsonString === lastEmittedChange.current) return;

        lastEmittedChange.current = jsonString;

        onChange(json, _editor.getText());
      },
      onFocus: handleFocus,
      onBlur: () => onBlur?.(),
      editorProps: {
        attributes: {
          autocomplete: disableBrowserAutocomplete ? "off" : "on",
          ...(testId ? { "data-testid": testId } : {}),
        },
        handlePaste(_, event) {
          const file = event.clipboardData?.files?.[0];

          if (file && file.type.startsWith("image/") && onPasteImage) {
            onPasteImage?.(event);
            return true;
          }

          return false;
        },
        handleKeyDown(_, event) {
          if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
            event.preventDefault();
            onSubmit?.();
            return true;
          }
        },
      },
    });

    // If parent changes value (e.g. load doc, undo outside, collaboration), push it into the editor.
    useEffect(() => {
      if (!editor) return;

      // If the change came from the editor itself, do nothing.
      if (JSON.stringify(value) === lastEmittedChange.current) return;

      // Update ref first so onUpdate sees the latest value and skips the duplicate emit
      lastEmittedChange.current = JSON.stringify(value);

      // Update editor without creating another history step
      editor.commands.setContent(value);
    }, [editor, value]);

    useEffect(() => {
      if (!editor) return;

      editor.setOptions({
        editable: !disabled,
      });
    }, [disabled, editor]);

    return (
      <TextEditorRoot disabled={disabled} className={classes.root}>
        <EditorContent ref={ref} editor={editor} />
        {placeholder && (editor?.isEmpty ?? true) && (
          <div className="placeholder">{placeholder}</div>
        )}
      </TextEditorRoot>
    );
  },
);
